import {
  faCheck,
  faLink,
  faLinkSlash,
  faTrash,
  faUserGraduate,
  faUserPlus
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link } from '@tanstack/react-router'
import clsx from 'clsx'
import { usePostHog } from 'posthog-js/react'
import type { MouseEvent, ReactElement } from 'react'
import { useContext, useMemo } from 'react'
import { getApiError } from '~/api/problem'
import { getApiViolations } from '~/api/utils'
import { EditorInput, Form, TextInput } from '~/components/Form'
import { ButtonGroup } from '~/components/Global/Blocks/ButtonGroup/ButtonGroup'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import { Alert, AlertVariation } from '~/components/Global/Interactions/Alert'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import { AddToOrderAction } from '~/components/Scopes/Actions/AddToOrderAction'
import {
  applicantModalAsideSection,
  applicantModalAsideSectionHighlighted,
  applicantModalAsideSectionTitle,
  applicantModalAsideSectionTitleWithButton,
  applicantModalOrderNote
} from '~/components/Scopes/Applicants/ApplicantModal/ApplicantModal.css'
import { ApplicantIntegrationFields } from '~/domains/applicants/components/ApplicantIntegrationFields'
import { ApplicantErrorType } from '~/domains/applicants/errors'
import { useConfig } from '~/domains/configuration/queries/config'
import { useOffer } from '~/domains/offers/queries/useOffer'
import { OrderStatusDropdown } from '~/domains/orders/components/OrderStatusDropdown'
import {
  type OrderClientHead,
  useOrderHead
} from '~/domains/orders/queries/useOrderHead'
import { formatOrderTitle } from '~/domains/orders/utils'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import type { TaxonomyItems } from '~/domains/taxonomies/types'
import { classicLink } from '~/styles/app.css'
import { isHtmlEmpty, toast } from '~/utils'
import { AsideActionContext } from '~/utils/contextes'
import { formatName } from '~/utils/names'
import { captureApplicationAction } from '../analytics'
import { useAcceptApplication } from '../mutations/acceptApplication'
import { useDeclineApplication } from '../mutations/declineApplication'
import { useIntegrateApplication } from '../mutations/integrateApplication'
import { useLinkApplicantMatch } from '../mutations/linkApplicantMatch'
import { useUnlinkApplicant } from '../mutations/unlinkApplicant'
import type { ApplicationInfosResponse } from '../types'
import { actionsContainer } from './ApplicationActions.css'
import { ApplicationIntegrationIdentityFields } from './ApplicationIntegrationIdentityFields'
import {
  type IntegrationFormValues,
  integrationFormSchema
} from './applicationIntegrationSchema'

type ApplicationActionsProps = {
  application: ApplicationInfosResponse
  applicantId?: number | null
  showOrder?: boolean
  onApplicantLinked: (applicantId: number | null) => void
  onProcessed?: () => void
}

export const ApplicationActions = ({
  application,
  applicantId,
  showOrder = false,
  onApplicantLinked,
  onProcessed
}: ApplicationActionsProps) => {
  const posthog = usePostHog()
  const { setAsideAction } = useContext(AsideActionContext)
  const { data: legacyUrl = '' } = useConfig('organization.legacy_url')

  const { data: orderStatuses } = useTaxonomy('applicant_order_statuses')
  const { data: autoEmailConfig } = useConfig(
    'application.response_auto_enabled'
  )
  const { data: defaultOfferQualificationConfig = true } = useConfig(
    'application.integration_default_offer_qualifications'
  )
  const { data: mandatoryQualification = true } = useConfig(
    'mandatory_applicant.qualification'
  )
  const { data: mandatoryEmail = true } = useConfig('mandatory_applicant.email')
  const { data: autoAdvanceAfterDecision = false } = useConfig(
    'application.auto_advance_after_decision'
  )
  const hasOffer =
    application?.offer !== null && application?.offer !== undefined

  const { data: offer } = useOffer(application?.offer?.id)

  const orderId = application.order?.id
  const hasOrder = !!orderId

  const { data: orderData } = useOrderHead<
    OrderClientHead & { qualifications: TaxonomyItems<'qualifications'> }
  >({
    orderId: showOrder && hasOrder ? orderId : undefined,
    additionalFields: ['qualifications']
  })

  const linkApplicantMatchMutation = useLinkApplicantMatch()
  const unlinkApplicantMutation = useUnlinkApplicant()
  const integrateApplicationMutation = useIntegrateApplication()
  const acceptApplicationMutation = useAcceptApplication({
    orderId
  })
  const declineApplicationMutation = useDeclineApplication({
    orderId
  })

  const defaultQualifications = useMemo(() => {
    if (!defaultOfferQualificationConfig) {
      return []
    }

    if (offer?.qualification?.id) {
      return [String(offer.qualification.id)]
    }

    return []
  }, [defaultOfferQualificationConfig, offer?.qualification?.id])

  const defaultSegmentations = useMemo(
    () =>
      offer?.segmentations?.map((segmentation) => String(segmentation.id)) ??
      [],
    [offer?.segmentations]
  )

  const applicationAddress = application?.metadata?.applicantLocation

  const defaultIntegrationValues = useMemo(
    (): IntegrationFormValues => ({
      email: application?.email || '',
      firstname: application?.firstname || '',
      lastname: application?.lastname || '',
      phone: application?.phone || '',
      address: applicationAddress
        ? {
            line1: applicationAddress.line ?? '',
            postcode: applicationAddress.postcode,
            city: applicationAddress.city,
            country: applicationAddress.country
          }
        : null,
      qualifications: defaultQualifications,
      segmentations: defaultSegmentations
    }),
    [
      application,
      applicationAddress,
      defaultQualifications,
      defaultSegmentations
    ]
  )

  if (!application) {
    return null
  }

  const handleDecisionSuccess = () => {
    if (autoAdvanceAfterDecision) {
      onProcessed?.()
    }
  }

  const handleLinkApplicantMatch = () => {
    if (!application.applicantMatchFound) return

    linkApplicantMatchMutation.mutate(
      {
        applicationId: application.id,
        applicantId: application.applicantMatchFound.id
      },
      {
        onSuccess: () => {
          captureApplicationAction(posthog, 'link_existing', {
            applicationId: application.id
          })
          const matchedApplicant = application.applicantMatchFound
          if (matchedApplicant) {
            onApplicantLinked(matchedApplicant.id)
          }
        },
        onError: (error) => {
          if (error.httpStatus === 403) {
            toast(
              'Accès refusé',
              "Vous n'avez pas les droits pour modifier ce candidat",
              ToastType.Danger
            )
            return
          }
          toast(
            'Erreur lors de la liaison',
            'Une erreur est survenue lors de la liaison du candidat',
            ToastType.Danger
          )
        }
      }
    )
  }

  const normalizeOptionalField = (value: string): string | undefined => {
    const trimmedValue = value.trim()
    return trimmedValue === '' ? undefined : trimmedValue
  }

  const handleIntegrateApplication = (data: IntegrationFormValues) => {
    if (mandatoryQualification && data.qualifications.length === 0) {
      toast(
        'Qualification requise',
        "Veuillez sélectionner au moins une qualification avant d'intégrer le candidat",
        ToastType.Danger
      )
      return
    }
    integrateApplicationMutation.mutate(
      {
        applicationId: application.id,
        email: data.email?.trim() || '',
        firstname: normalizeOptionalField(data.firstname),
        lastname: normalizeOptionalField(data.lastname),
        phone: normalizeOptionalField(data.phone),
        address: data.address ?? undefined,
        qualifications: data.qualifications,
        segmentations: data.segmentations
      },
      {
        onSuccess: (applicant) => {
          captureApplicationAction(posthog, 'integrate_new', {
            applicationId: application.id,
            hasEmail: Boolean(data.email),
            qualificationsCount: data.qualifications.length,
            segmentationsCount: data.segmentations.length,
            qualificationsEdited:
              [...data.qualifications].sort().join(',') !==
              [...defaultQualifications].sort().join(','),
            segmentationsEdited:
              [...data.segmentations].sort().join(',') !==
              [...defaultSegmentations].sort().join(',')
          })
          onApplicantLinked(applicant.id)
          toast(
            'Candidat intégré au vivier',
            hasOffer
              ? 'Vous pouvez désormais accepter ou non sa candidature'
              : '',
            ToastType.Success
          )
        },
        onError: (error) => {
          const problem = getApiError(error)
          if (problem?.type === ApplicantErrorType.Duplicate) {
            const duplicateIds = Array.isArray(problem.extensions.duplicateIds)
              ? problem.extensions.duplicateIds
              : []
            const firstDuplicate = duplicateIds[0]
            toast(
              'Doublon détecté',
              firstDuplicate !== undefined
                ? `Un doublon avec cet email ou téléphone (#${firstDuplicate}) existe déjà dans le vivier`
                : 'Un doublon avec cet email ou téléphone existe déjà dans le vivier',
              ToastType.Danger
            )
            return
          }
          if (error.httpStatus === 403) {
            // Handled by API error handler
            return
          }
          const hasMissingQualifications = getApiViolations(error).some((v) =>
            v.message.includes('qualifications')
          )
          if (hasMissingQualifications) {
            toast(
              'Qualification requise',
              "Veuillez sélectionner au moins une qualification avant d'intégrer le candidat",
              ToastType.Danger
            )
            return
          }
          toast(
            "Erreur lors de l'intégration de la candidature",
            "Une erreur est survenue lors de l'intégration de la candidature",
            ToastType.Danger
          )
        }
      }
    )
  }

  const handleUnlinkApplicant = () => {
    unlinkApplicantMutation.mutate(
      { applicationId: application.id },
      {
        onSuccess: () => {
          onApplicantLinked(null)
        }
      }
    )
  }

  const handleAcceptApplication = (data: {
    orderStatus?: string
    note?: string
  }) => {
    const note = data.note && !isHtmlEmpty(data.note) ? data.note : undefined
    acceptApplicationMutation.mutate(
      {
        applicationId: application.id,
        orderStatus: data.orderStatus ? Number(data.orderStatus) : undefined,
        note
      },
      {
        onSuccess: () => {
          captureApplicationAction(posthog, 'accept', {
            applicationId: application.id,
            orderStatus: data.orderStatus ? Number(data.orderStatus) : null
          })
          handleDecisionSuccess()
        },
        onError: () => {
          toast(
            "Erreur lors de l'acceptation de la candidature",
            "Une erreur est survenue lors de l'acceptation de la candidature",
            ToastType.Danger
          )
        }
      }
    )
  }

  const handleDeclineApplication = (e: MouseEvent) => {
    e.preventDefault()

    declineApplicationMutation.mutate(
      { applicationId: application.id },
      {
        onSuccess: () => {
          captureApplicationAction(posthog, 'decline', {
            applicationId: application.id
          })
          handleDecisionSuccess()
        },
        onError: () => {
          toast(
            'Erreur lors du refus de la candidature',
            'Une erreur est survenue lors du refus de la candidature',
            ToastType.Danger
          )
        }
      }
    )
  }

  const filteredOrderStatuses =
    orderStatuses?.filter((status) => status.specialStatus !== 'accepted') || []

  const defaultOrderStatus = filteredOrderStatuses.find(
    (status) => status.specialStatus === 'selected'
  )

  const withAutoEmailTooltip = (
    text: string,
    button: ReactElement
  ): ReactElement =>
    autoEmailConfig !== false ? (
      <Tooltip text={text} direction="bottom" as="span">
        {button}
      </Tooltip>
    ) : (
      button
    )

  return (
    <div
      className={clsx(
        applicantModalAsideSection,
        applicantModalAsideSectionHighlighted
      )}
    >
      <div
        className={clsx(
          applicantModalAsideSectionTitle,
          applicantModalAsideSectionTitleWithButton
        )}
      >
        <div>
          <FontAwesomeIcon icon={faUserGraduate} /> Candidature
        </div>
        {applicantId && (
          <ButtonGroup align="right">
            <Button
              type="button"
              variation={ButtonVariation.DangerInversed}
              buttonSize="small"
              onClick={handleUnlinkApplicant}
              disabled={unlinkApplicantMutation.isPending}
            >
              <FontAwesomeIcon icon={faLinkSlash} />
              Délier le candidat
            </Button>
          </ButtonGroup>
        )}
      </div>
      <div className={actionsContainer}>
        {/* Applicant match warning */}
        {application.applicantMatchFound && !applicantId && (
          <Alert
            variation={AlertVariation.Warning}
            message={
              <>
                Un candidat correspondant a été trouvé :{' '}
                <a
                  href={`${legacyUrl}/admin/candidats/edit/${application.applicantMatchFound.id}`}
                  className={classicLink}
                  target="_blank"
                  rel="noopener"
                >
                  {formatName(application.applicantMatchFound)}
                </a>
              </>
            }
          >
            <Button
              variation={ButtonVariation.PrimaryInversed}
              onClick={handleLinkApplicantMatch}
              buttonSize="small"
              disabled={linkApplicantMatchMutation.isPending}
            >
              <FontAwesomeIcon icon={faLink} />
              Lier le candidat
            </Button>
          </Alert>
        )}

        {/* Integration form */}
        {!applicantId && !application.isCompleted && (
          <Form
            key={`integration-${application.id}`}
            defaultValues={defaultIntegrationValues}
            schema={integrationFormSchema}
            onSubmit={handleIntegrateApplication}
          >
            <TextInput
              label="Email"
              name="email"
              type="email"
              required={mandatoryEmail}
            />
            <ApplicationIntegrationIdentityFields />

            <ApplicantIntegrationFields />
            <ButtonGroup align="right">
              {withAutoEmailTooltip(
                'Un email automatique sera envoyé au candidat si vous choisissez de rejeter sa candidature.',
                <Button
                  type="button"
                  variation={ButtonVariation.Danger}
                  onClick={handleDeclineApplication}
                  disabled={declineApplicationMutation.isPending}
                >
                  <FontAwesomeIcon icon={faTrash} />
                  Rejeter
                </Button>
              )}
              <Button
                type="submit"
                variation={ButtonVariation.Primary}
                disabled={integrateApplicationMutation.isPending}
              >
                <FontAwesomeIcon icon={faUserPlus} />
                Intégrer au vivier
              </Button>
            </ButtonGroup>
          </Form>
        )}

        {applicantId && !application.isCompleted && !hasOffer && (
          <ButtonGroup align="right">
            <Alert
              variation={AlertVariation.Info}
              message="Ce candidat dans le vivier ayant réalisé une candidature spontanée, vous pouvez uniquement marquer sa candidature comme traitée ou l'archiver."
              onDismiss={false}
            />
            {withAutoEmailTooltip(
              "Un email automatique sera envoyé au candidat lors de l'archivage de sa candidature.",
              <Button
                variation={ButtonVariation.Danger}
                onClick={handleDeclineApplication}
                disabled={declineApplicationMutation.isPending}
              >
                <FontAwesomeIcon icon={faTrash} />
                Archiver le candidat
              </Button>
            )}
            <Button
              variation={ButtonVariation.Success}
              disabled={acceptApplicationMutation.isPending}
              onClick={(e) => {
                e.preventDefault()
                acceptApplicationMutation.mutate(
                  { applicationId: application.id },
                  { onSuccess: handleDecisionSuccess }
                )
              }}
            >
              <FontAwesomeIcon icon={faCheck} />
              Traitée
            </Button>
          </ButtonGroup>
        )}

        {applicantId &&
          !application.isCompleted &&
          hasOffer &&
          (!hasOrder || filteredOrderStatuses.length > 0) && (
            <Form
              key={`accept-${application.id}`}
              defaultValues={
                hasOrder
                  ? { orderStatus: String(defaultOrderStatus?.id), note: '' }
                  : undefined
              }
              onSubmit={handleAcceptApplication}
            >
              {!hasOrder && applicantId != null && (
                <Alert
                  variation={AlertVariation.Warning}
                  message="Aucune commande n'est associée à l'offre de
                      cette candidature."
                  onDismiss={false}
                >
                  <Button
                    variation={ButtonVariation.Link}
                    buttonSize="small"
                    type="button"
                    onClick={() => {
                      setAsideAction({
                        action: (
                          <AddToOrderAction applicantIds={[applicantId]} />
                        ),
                        position: 'bottom',
                        availablePositions: ['bottom', 'modal']
                      })
                    }}
                  >
                    Associer le candidat à une commande
                  </Button>
                </Alert>
              )}

              {hasOrder && (
                <>
                  <OrderStatusDropdown
                    label={
                      <>
                        Associer à la commande
                        {(orderData?.jobTitle ||
                          (orderData?.qualifications &&
                            orderData.qualifications.length > 0)) && (
                          <>
                            {' '}
                            <Link
                              to={'/orders/$orderId'}
                              params={{ orderId: String(orderData?.id) }}
                              className={classicLink}
                            >
                              {formatOrderTitle(orderData)}
                            </Link>
                          </>
                        )}
                      </>
                    }
                    name="orderStatus"
                    required
                    statuses={filteredOrderStatuses}
                  />
                  <div className={applicantModalOrderNote}>
                    <EditorInput
                      label=""
                      name="note"
                      showBottomMenu={false}
                      isReduced
                      autofocus={false}
                      placeholder="Saisir un commentaire sur la commande..."
                    />
                  </div>
                </>
              )}

              <ButtonGroup align="right">
                {withAutoEmailTooltip(
                  "Un email automatique sera envoyé au candidat lors de l'archivage de sa candidature.",
                  <Button
                    type="button"
                    variation={ButtonVariation.Danger}
                    onClick={handleDeclineApplication}
                    disabled={declineApplicationMutation.isPending}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                    Archiver
                  </Button>
                )}
                {hasOrder ? (
                  withAutoEmailTooltip(
                    "Un email automatique sera envoyé au candidat lors de l'association de sa candidature.",
                    <Button
                      type="submit"
                      variation={ButtonVariation.Success}
                      disabled={acceptApplicationMutation.isPending}
                    >
                      <FontAwesomeIcon icon={faCheck} />
                      Associer
                    </Button>
                  )
                ) : (
                  <Button
                    type="button"
                    variation={ButtonVariation.Success}
                    disabled={acceptApplicationMutation.isPending}
                    onClick={(e) => {
                      e.preventDefault()
                      acceptApplicationMutation.mutate(
                        { applicationId: application.id },
                        { onSuccess: handleDecisionSuccess }
                      )
                    }}
                  >
                    <FontAwesomeIcon icon={faCheck} />
                    Traitée
                  </Button>
                )}
              </ButtonGroup>
            </Form>
          )}
      </div>
    </div>
  )
}
